import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Anchor } from 'components/base';
import { CloudinaryPicture, WIDTH_LADDERS } from 'components/rich';

import PdpIcon from '../product/pdpIcon';

// Three tiles per row on desktop, two on tablets, a 96px thumbnail in the phone row.
const DEFAULT_SIZES = '(min-width: 992px) 384px, (min-width: 768px) 50vw, 96px';

/**
 * Product tile for landing pages (Pencil "Photo Books — Redesign" book cards):
 * photo with tag and price chip, title, one-liner, spec list, a "Good for"
 * use case and a full-width CTA. Phones get a compact row from the same
 * markup: thumbnail, tag, title, summary, price and an arrow.
 *
 * The title link carries the SEO anchor text and comes first in the DOM, a
 * pseudo element stretches it over the whole tile.
 */
const LandingProductTile = ({
  href,
  title,
  imageKey,
  sizes = DEFAULT_SIZES,
  tag,
  price,
  originalPrice,
  saleBadge,
  priceFrom = false,
  line,
  summary,
  specs,
  goodFor,
  ctaLabel,
  ctaOutline = false,
  onClick,
  className,
}) => (
  <article className={classNames('lpt', className)}>
    <div className="lpt-media">
      <CloudinaryPicture
        imageKey={imageKey}
        widths={WIDTH_LADDERS.landing}
        sizes={sizes}
        loading="lazy"
      />
    </div>

    <div className="lpt-body">
      <div className="lpt-head">
        {tag && (
          <p className={classNames('lpt-tag', `lpt-tag--${tag.tone ?? 'green'}`)}>{tag.label}</p>
        )}
        {saleBadge && <span className="lpt-sale-badge coupon-reveal">{saleBadge}</span>}
        <h3 className="lpt-title">
          <Anchor href={href} className="lpt-title-link" onClick={onClick}>
            {title}
          </Anchor>
        </h3>
        <p className="lpt-line">{line}</p>
        {summary && <p className="lpt-summary">{summary}</p>}
      </div>

      {price && (
        <p className="lpt-price">
          {priceFrom && (
            <span className="lpt-price-prefix">
              <span className="lpt-desktop">Starting at</span>
              <span className="lpt-phone">From</span>
            </span>
          )}
          {originalPrice ? (
            <span
              className="lpt-price-row coupon-reveal"
              role="img"
              aria-label={`${price} with coupon, was ${originalPrice}`}
            >
              <span className="lpt-price-value text-success" aria-hidden="true">
                {price}
              </span>
              <s className="lpt-price-original" aria-hidden="true">
                {originalPrice}
              </s>
            </span>
          ) : (
            <span className="lpt-price-value">{price}</span>
          )}
        </p>
      )}

      {specs?.length > 0 && (
        <ul className="lpt-specs">
          {specs.map(({ icon, label }) => (
            <li key={label}>
              <PdpIcon name={icon} size={16} />
              {label}
            </li>
          ))}
        </ul>
      )}

      {goodFor && (
        <div className="lpt-good-for">
          <p className="lpt-good-for-label">Good for</p>
          <p>{goodFor}</p>
        </div>
      )}

      {/* Same destination as the title link, kept out of the tab order to avoid a duplicate stop. */}
      <span className={classNames('lpt-cta', { 'lpt-cta--outline': ctaOutline })} aria-hidden="true">
        {ctaLabel}
        <PdpIcon name="arrowRight" size={16} />
      </span>
    </div>

    <span className="lpt-go" aria-hidden="true">
      <PdpIcon name="chevronRight" size={15} />
    </span>
  </article>
);

LandingProductTile.propTypes = {
  href: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // Key in the landing/ image folder of parabo-product-images.
  imageKey: PropTypes.string.isRequired,
  sizes: PropTypes.string,
  tag: PropTypes.shape({
    label: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(['green', 'terracotta']),
  }),
  // Formatted price, e.g. "$15".
  price: PropTypes.string,
  originalPrice: PropTypes.string,
  saleBadge: PropTypes.string,
  priceFrom: PropTypes.bool,
  line: PropTypes.string.isRequired,
  // Phone row only, e.g. "6x6 or 8x8 in · 26–100 pages".
  summary: PropTypes.string,
  specs: PropTypes.arrayOf(
    PropTypes.shape({ icon: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ),
  goodFor: PropTypes.string,
  ctaLabel: PropTypes.string.isRequired,
  ctaOutline: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default LandingProductTile;
